import { Button, ButtonProps, Divider, Flex } from 'antd'
import { ReactNode } from 'react'

import { useLocales } from 'src/locales'

import { SaveConfirm } from '../SaveConfirm'

type Props = {
  onCancel?: () => void
  onSave: () => void
  /** Confirmation title; defaults to "<save label>?". */
  saveConfirmTitle?: string
  /** Save button text; defaults to common.save. */
  saveLabel?: string
  /** Cancel button text; defaults to common.cancel. */
  cancelLabel?: string
  loading?: boolean
  disabled?: boolean
  saveButtonProps?: ButtonProps
  /** Hides the whole footer (e.g. read-only view). */
  hidden?: boolean
  /** Extra buttons rendered between Cancel and Save (rarely needed). */
  extra?: ReactNode
}

/** The Cancel + Save actions at the bottom of a form page, the same on every
 *  form. Save asks for confirmation through SaveConfirm. */
export const FormFooter = ({
  onCancel,
  onSave,
  saveConfirmTitle,
  saveLabel,
  cancelLabel,
  loading,
  disabled,
  saveButtonProps,
  hidden,
  extra
}: Props) => {
  const { t } = useLocales()

  if (hidden) return null

  const label = saveLabel ?? t('common.save')

  return (
    <>
      <Divider />

      <Flex justify="center" gap={16} wrap="wrap">
        {onCancel && (
          <Button onClick={onCancel}>
            {cancelLabel ?? t('common.cancel')}
          </Button>
        )}

        {extra}

        <SaveConfirm
          title={saveConfirmTitle ?? `${label}?`}
          onConfirm={onSave}
          placement="top"
          okButtonProps={{ loading }}
          disabled={disabled}
        >
          <Button
            type="primary"
            loading={loading}
            disabled={disabled}
            {...saveButtonProps}
          >
            {label}
          </Button>
        </SaveConfirm>
      </Flex>
    </>
  )
}
